import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  BarChart3, Users, Images, MessageSquare, Mail, Settings2, Send, Trash2, Upload,
  CheckCircle2, AlertTriangle, XCircle, ExternalLink, KeyRound, CalendarX2, Euro, Phone, Clock, Clock3, RefreshCw, Wallet,
  FileText, Download, Activity, RotateCcw, Paperclip, Megaphone, Ticket, UserCheck, ShoppingBag, UserPlus, CalendarPlus, Camera,
} from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import api, { API } from "@/lib/api";
import { loadPlanPrices } from "@/lib/plans";
import { CLOSURE_ICONS, CLOSURE_ICON_KEYS } from "@/lib/closureIcons";
import { ANNOUNCEMENT_ICONS, ANNOUNCEMENT_ICON_KEYS } from "@/lib/announcementIcons";
import SmsHistoryDialog from "@/components/admin/SmsHistoryDialog";
import EmailHistoryDialog from "@/components/admin/EmailHistoryDialog";

const card = "rounded-2xl bg-white border border-brand/10 p-5 shadow-[0_8px_30px_rgba(134,77,99,0.06)]";
const inputCls = "rounded-xl border-brand/20 bg-white h-10 focus-visible:ring-brand";

// ---------------------------------------------------------------- Analytics
export const DevAnalytics = () => {
  const { t, lang } = useLang();
  const d = t.admin.dev;
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/analytics").then((r) => setData(r.data)).catch(() => toast.error("Error"));
  }, []);

  if (!data) return <p className="text-brand-ink/50 py-10 text-center">...</p>;

  const pct = (cur, prev) => {
    if (!prev) return cur ? "+100%" : "0%";
    const v = Math.round(((cur - prev) / prev) * 100);
    return `${v > 0 ? "+" : ""}${v}%`;
  };

  const MiniBars = ({ series, color }) => {
    const max = Math.max(1, ...series.map((s) => s.count));
    return (
      <div className="flex items-end gap-1 h-16 mt-3">
        {series.map((s) => (
          <div key={s.date} className="flex-1 flex flex-col items-center gap-0.5" title={`${s.date}: ${s.count}`}>
            <div className={`w-full rounded-t ${color}`} style={{ height: `${Math.max(6, (s.count / max) * 100)}%` }} />
          </div>
        ))}
      </div>
    );
  };

  const StatBox = ({ label, total, prev, series, color, testid }) => (
    <div className={card} data-testid={testid}>
      <div className="flex items-baseline justify-between">
        <p className="text-xs font-bold uppercase tracking-wide text-brand-ink/50">{label}</p>
        <span className="text-[11px] font-bold text-brand-ink/40">{d.vs_prev}: {pct(total, prev)}</span>
      </div>
      <p className="font-heading text-3xl font-bold text-brand-ink mt-1">{total}</p>
      <MiniBars series={series} color={color} />
    </div>
  );

  return (
    <div className="space-y-5" data-testid="dev-analytics">
      <div className="grid gap-4 md:grid-cols-3">
        <StatBox label={d.visits} total={data.visits.total} prev={data.visits.prev_total} series={data.visits.series} color="bg-brand/70" testid="dev-stat-visits" />
        <StatBox label={d.bookings_14d} total={data.bookings.total} prev={data.bookings.prev_total} series={data.bookings.series} color="bg-bamboo/70" testid="dev-stat-bookings" />
        <StatBox label={d.messages_14d} total={data.messages.total} prev={data.messages.prev_total} series={data.messages.series} color="bg-brand-ink/50" testid="dev-stat-messages" />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className={card} data-testid="dev-per-page">
          <p className="font-heading text-lg font-bold text-brand-ink mb-3">{d.per_page}</p>
          {data.per_page.length === 0 ? <p className="text-sm text-brand-ink/50">—</p> : (
            <div className="space-y-2">
              {data.per_page.map(([path, count]) => (
                <div key={path} className="flex items-center justify-between text-sm">
                  <span className="font-semibold text-brand-ink/80">{path}</span>
                  <span className="font-bold text-brand">{count}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={card} data-testid="dev-weekly-revenue">
          <p className="font-heading text-lg font-bold text-brand-ink mb-3">{d.weekly_revenue}</p>
          {data.weekly_revenue.length === 0 ? <p className="text-sm text-brand-ink/50">—</p> : (
            <div className="space-y-2">
              {data.weekly_revenue.map((w) => (
                <div key={w.week} className="flex items-center justify-between text-sm">
                  <span className="font-semibold text-brand-ink/80">{w.week}</span>
                  <span className="font-bold text-bamboo-deep">€{w.revenue}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className={card} data-testid="dev-month-compare">
        <p className="font-heading text-lg font-bold text-brand-ink mb-3">{d.month_compare}</p>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-xs font-bold text-brand-ink/50 uppercase">{d.this_month} ({data.month.this})</p>
            <p className="mt-1 text-brand-ink">{d.bookings}: <b>{data.month.bookings_this}</b> · {d.revenue}: <b>€{data.month.revenue_this}</b></p>
          </div>
          <div>
            <p className="text-xs font-bold text-brand-ink/50 uppercase">{d.last_month} ({data.month.last})</p>
            <p className="mt-1 text-brand-ink">{d.bookings}: <b>{data.month.bookings_last}</b> · {d.revenue}: <b>€{data.month.revenue_last}</b></p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------- Customers CRM + targeted bulk
// Shared by Dev console (can send bulk) and Groomer dashboard (no bulk).
const fmtDateTime = (iso, lang) =>
  iso ? new Date(iso).toLocaleString(lang === "el" ? "el-GR" : "en-GB", { dateStyle: "short", timeStyle: "short" }) : "—";

const crmTheme = () => ({
  card: "rounded-2xl bg-white border border-brand/10 p-5 shadow-sm",
  txt: "text-brand-ink",
  txt70: "text-brand-ink/70",
  txt60: "text-brand-ink/60",
  txt50: "text-brand-ink/50",
  txt40: "text-brand-ink/40",
  acc: "text-brand",
  rev: "text-bamboo-deep",
  inp: "rounded-xl border-brand/20 bg-white h-10 focus-visible:ring-brand",
  row: "border-brand/5 hover:bg-brand-soft/30",
  box: "bg-stone-alt/50",
  chipG: "bg-brand-soft text-brand",
  chipS: "bg-bamboo-soft text-bamboo-deep",
  chanWrap: "border-brand/20",
  chanOff: "text-brand-ink/60",
  textarea: "rounded-xl border-brand/20 bg-white min-h-[90px]",
  noteBox: "bg-stone-alt/60 border-brand/10",
  drawer: "bg-brand-cream border-brand/10",
  drawerHead: "bg-brand-cream/95 border-brand/10",
  closeBtn: "bg-brand/10 text-brand hover:bg-brand-soft",
  headBorder: "border-brand/10",
});

export const CustomerProfileDrawer = ({ phone, onClose, isDev = false }) => {
  const { t, lang } = useLang();
  const d = t.admin.dev;
  const a = t.admin;
  const m = t.mapp;
  const c = crmTheme();
  const [profile, setProfile] = useState(null);
  const [newNote, setNewNote] = useState("");
  const [editNote, setEditNote] = useState({ id: null, text: "" });
  const [busyAction, setBusyAction] = useState(false);
  const [couponForm, setCouponForm] = useState({ kind: "percent", amount: 20, expires: "" });
  const [pastOpen, setPastOpen] = useState(false);
  const [pastForm, setPastForm] = useState({ type: "grooming", date: "", time: "", service: "", plan: "medium", nagayu: false, price: "", notes: "" });

  const addPast = async () => {
    if (!pastForm.date) return;
    setBusyAction(true);
    try {
      await api.post(`/admin/customers/${encodeURIComponent(phone)}/past-booking`, {
        type: pastForm.type,
        date: pastForm.date,
        time: pastForm.time || null,
        service: pastForm.service || null,
        plan: pastForm.plan,
        nagayu: pastForm.nagayu,
        price: pastForm.price ? Number(pastForm.price) : null,
        notes: pastForm.notes || null,
      });
      toast.success(d.past_saved);
      setPastOpen(false);
      setPastForm({ type: "grooming", date: "", time: "", service: "", plan: "medium", nagayu: false, price: "", notes: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setBusyAction(false);
    }
  };

  const load = useCallback(() => {
    api.get(`/admin/customers/${encodeURIComponent(phone)}/profile`).then((r) => setProfile(r.data)).catch(() => toast.error("Error"));
  }, [phone]);
  useEffect(load, [load]);

  const sendReset = async () => {
    if (!profile?.account) return;
    setBusyAction(true);
    try {
      await api.post(`/admin/accounts/${profile.account.id}/send-reset`);
      toast.success(d.account_reset_sent);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setBusyAction(false);
    }
  };

  const deleteAccount = async () => {
    if (!profile?.account || !window.confirm(d.account_delete_confirm)) return;
    setBusyAction(true);
    try {
      await api.delete(`/admin/accounts/${profile.account.id}`);
      toast.success(d.account_deleted);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setBusyAction(false);
    }
  };

  const createCoupon = async () => {
    if (!couponForm.amount || !couponForm.expires) return;
    setBusyAction(true);
    try {
      const res = await api.post(`/admin/customers/${encodeURIComponent(phone)}/coupon`, couponForm);
      toast.success(`${d.coupon_created}: ${res.data.code}`);
      setCouponForm({ kind: "percent", amount: 20, expires: "" });
      load();
      if (window.confirm(d.coupon_sms_ask)) {
        try {
          await api.post(`/admin/customers/${encodeURIComponent(phone)}/coupon/send-sms`, { code: res.data.code });
          toast.success(d.coupon_sms_sent);
        } catch (err) {
          toast.error(err.response?.data?.detail || "Error");
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setBusyAction(false);
    }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    try {
      await api.post(`/admin/customers/${encodeURIComponent(phone)}/notes`, { text: newNote.trim() });
      setNewNote("");
      load();
    } catch { toast.error("Error"); }
  };

  const setConsent = async (status) => {
    setBusyAction(true);
    try {
      await api.post(`/admin/customers/${encodeURIComponent(phone)}/photo-consent`, { status });
      toast.success(m.consent_saved);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setBusyAction(false);
    }
  };
  const saveNote = async () => {
    try {
      await api.put(`/admin/notes/${editNote.id}`, { text: editNote.text.trim() });
      setEditNote({ id: null, text: "" });
      load();
    } catch { toast.error("Error"); }
  };
  const delNote = async (id) => {
    try { await api.delete(`/admin/notes/${id}`); load(); } catch { toast.error("Error"); }
  };

  return (
    <div className="fixed inset-0 z-[70]" data-testid="customer-profile-drawer">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className={`absolute right-0 top-0 h-full w-full max-w-md border-l shadow-2xl overflow-y-auto ${c.drawer}`}>
        <div className={`sticky top-0 backdrop-blur border-b px-5 py-4 flex items-center justify-between z-10 ${c.drawerHead}`}>
          <p className={`font-heading text-lg font-bold ${c.txt}`}>{d.profile_title}</p>
          <button type="button" onClick={onClose} className={`grid place-items-center h-9 w-9 rounded-full ${c.closeBtn}`} data-testid="profile-close" aria-label="Close">
            <XCircle className="h-5 w-5" />
          </button>
        </div>
        {!profile ? (
          <p className={`${c.txt50} text-center py-16`}>...</p>
        ) : (
          <div className="p-5 space-y-6">
            <div>
              <p className={`font-heading text-2xl font-bold ${c.txt}`} data-testid="profile-name">{profile.name || "—"}</p>
              <div className="mt-2 space-y-1 text-sm">
                <a href={`tel:${profile.phone}`} className={`flex items-center gap-2 ${c.acc} font-semibold no-a11y-underline`}><Phone className="h-4 w-4" />{profile.phone}</a>
                {profile.email && <span className={`flex items-center gap-2 ${c.txt70}`}><Mail className="h-4 w-4" />{profile.email}</span>}
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                <div className={`rounded-xl ${c.box} p-2.5`}><p className={`text-lg font-bold ${c.txt}`} data-testid="profile-bookings">{profile.bookings}</p><p className={`text-[10px] uppercase tracking-wide ${c.txt50}`}>{d.col_bookings}</p></div>
                <div className={`rounded-xl ${c.box} p-2.5`}><p className={`text-lg font-bold ${c.rev}`} data-testid="profile-revenue">€{profile.revenue}</p><p className={`text-[10px] uppercase tracking-wide ${c.txt50}`}>{d.col_revenue}</p></div>
                <div className={`rounded-xl ${c.box} p-2.5`}><p className={`text-lg font-bold ${c.txt}`} data-testid="profile-last-visit">{profile.last_visit || "—"}</p><p className={`text-[10px] uppercase tracking-wide ${c.txt50}`}>{d.last_visit_label}</p></div>
              </div>
            </div>

            {/* Registered account info + dev actions */}
            <div>
              <p className={`text-xs font-bold uppercase tracking-wider ${c.txt50} mb-2`}>{d.account_badge}</p>
              {profile.account ? (
                <div className={`rounded-xl border px-3.5 py-3 ${c.noteBox}`} data-testid="profile-account">
                  <p className={`text-sm font-bold ${c.txt} flex items-center gap-2`}>
                    <UserCheck className={`h-4 w-4 ${c.acc}`} />
                    {profile.account.first_name} {profile.account.last_name || ""}
                  </p>
                  <p className={`text-xs ${c.txt60} mt-0.5`}>{profile.account.email} · {profile.account.phone}</p>
                  <p className={`text-[11px] ${c.txt40} mt-1`}>
                    {d.account_registered}: {fmtDateTime(profile.account.created_at, lang)}
                    {(profile.account.wishlist || []).length > 0 && ` · ${(profile.account.wishlist || []).length} ${d.wishlist_label}`}
                  </p>
                  {isDev && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button onClick={sendReset} disabled={busyAction} size="sm" variant="outline" className="rounded-full border-brand/30 text-brand font-bold h-8 text-xs" data-testid="account-send-reset-btn">
                        <KeyRound className="h-3.5 w-3.5 mr-1.5" /> {d.account_send_reset}
                      </Button>
                      <Button onClick={deleteAccount} disabled={busyAction} size="sm" variant="outline" className="rounded-full border-rose-300 text-rose-600 hover:bg-rose-50 font-bold h-8 text-xs" data-testid="account-delete-btn">
                        <Trash2 className="h-3.5 w-3.5 mr-1.5" /> {d.account_delete}
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <p className={`text-sm ${c.txt40} italic`} data-testid="profile-no-account">{d.account_none}</p>
              )}
            </div>

            {/* Photo/video publication consent */}
            {(() => {
              const pc = profile.photo_consent || { status: "none" };
              const st = pc.status || "none";
              const styles = {
                granted: "bg-emerald-100 text-emerald-700 border-emerald-200",
                declined: "bg-rose-100 text-rose-600 border-rose-200",
                revoked: "bg-amber-100 text-amber-700 border-amber-200",
                none: "bg-stone-100 text-stone-500 border-stone-200",
              };
              return (
                <div data-testid="profile-consent">
                  <p className={`text-xs font-bold uppercase tracking-wider ${c.txt50} mb-2 flex items-center gap-1.5`}>
                    <Camera className="h-3.5 w-3.5" /> {m.consent_label}
                  </p>
                  <div className={`rounded-xl border px-3.5 py-3 ${c.noteBox}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-xs font-bold px-3 py-1.5 rounded-full border ${styles[st]}`} data-testid="profile-consent-status">{m[`consent_status_${st}`]}</span>
                      {pc.at && <span className={`text-[11px] ${c.txt40}`} data-testid="profile-consent-at">{fmtDateTime(pc.at, lang)}{pc.by ? ` · ${pc.by}` : ""}</span>}
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button onClick={() => setConsent("granted")} disabled={busyAction} size="sm" variant="outline" className="rounded-full border-emerald-300 text-emerald-700 hover:bg-emerald-50 font-bold h-8 text-xs" data-testid="consent-grant-btn">{m.consent_set_yes}</Button>
                      <Button onClick={() => setConsent("declined")} disabled={busyAction} size="sm" variant="outline" className="rounded-full border-rose-300 text-rose-600 hover:bg-rose-50 font-bold h-8 text-xs" data-testid="consent-decline-btn">{m.consent_set_no}</Button>
                      <Button onClick={() => setConsent("revoked")} disabled={busyAction || st === "none"} size="sm" variant="outline" className="rounded-full border-amber-300 text-amber-700 hover:bg-amber-50 font-bold h-8 text-xs" data-testid="consent-revoke-btn">{m.consent_set_revoke}</Button>
                    </div>
                    {(profile.photo_consent_history || []).length > 0 && (
                      <div className="mt-3 space-y-1.5" data-testid="profile-consent-history">
                        <p className={`text-[10px] font-bold uppercase tracking-wider ${c.txt40}`}>{m.consent_history}</p>
                        {profile.photo_consent_history.slice(0, 5).map((h, i) => (
                          <p key={i} className={`text-[11px] ${c.txt50}`}>
                            {m[`consent_status_${h.status}`]} · {fmtDateTime(h.at, lang)}{h.actor ? ` · ${h.actor}` : ""}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Boutique orders summary */}
            {(profile.orders_count > 0) && (
              <div>
                <p className={`text-xs font-bold uppercase tracking-wider ${c.txt50} mb-2`}>{d.orders_title}</p>
                <div className="space-y-2" data-testid="profile-orders">
                  {profile.orders.map((o) => (
                    <div key={o.id} className={`flex items-center gap-3 rounded-xl ${c.box} px-3 py-2.5 text-sm`} data-testid={`profile-order-${o.order_number}`}>
                      <ShoppingBag className={`h-4 w-4 ${c.acc} shrink-0`} />
                      <div className="min-w-0">
                        <p className={`font-bold ${c.txt} font-mono text-[13px]`}>{o.order_number}</p>
                        <p className={`${c.txt50} text-xs`}>{o.items?.length || 0} × · {fmtDateTime(o.created_at, lang)}</p>
                      </div>
                      <span className={`ml-auto text-xs ${c.txt60} shrink-0`}>{t.account.order_status[o.status] || o.status} · €{o.total?.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Coupons (dev can grant manually) */}
            <div>
              <p className={`text-xs font-bold uppercase tracking-wider ${c.txt50} mb-2`}>{d.coupons_title}</p>
              <div className="space-y-2" data-testid="profile-coupons">
                {(profile.coupons || []).length === 0 && <p className={`text-sm ${c.txt40} italic`}>—</p>}
                {(profile.coupons || []).map((cp) => {
                  const expired = cp.expires < new Date().toISOString().slice(0, 10);
                  const state = cp.used ? d.coupon_used : expired ? d.coupon_expired : d.coupon_active;
                  return (
                    <div key={cp.code} className={`flex items-center gap-3 rounded-xl ${c.box} px-3 py-2 text-sm`} data-testid={`profile-coupon-${cp.code}`}>
                      <Ticket className={`h-4 w-4 ${cp.used || expired ? c.txt40 : "text-emerald-600"} shrink-0`} />
                      <span className={`font-mono font-bold ${c.txt}`}>{cp.code}</span>
                      <span className={`text-xs ${c.txt60}`}>{cp.kind === "percent" ? `-${cp.amount}%` : `-€${cp.amount}`} · {cp.expires}</span>
                      <span className={`ml-auto text-[10px] font-bold uppercase ${cp.used ? "text-brand-ink/40" : expired ? "text-rose-500" : "text-emerald-700"}`}>{state}</span>
                    </div>
                  );
                })}
              </div>
              {isDev && (
                <div className={`mt-3 rounded-xl border px-3.5 py-3 ${c.noteBox}`} data-testid="coupon-grant-form">
                  <p className={`text-xs font-bold ${c.txt70} flex items-center gap-1.5`}><Ticket className="h-3.5 w-3.5 text-brand" /> {d.coupon_new}</p>
                  <div className="mt-2 grid grid-cols-3 gap-2">
                    <Select value={couponForm.kind} onValueChange={(v) => setCouponForm({ ...couponForm, kind: v })}>
                      <SelectTrigger className={c.inp} data-testid="grant-coupon-kind"><SelectValue /></SelectTrigger>
                      <SelectContent className="z-[90]">
                        <SelectItem value="percent">%</SelectItem>
                        <SelectItem value="euro">€</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input type="number" min="1" max={couponForm.kind === "percent" ? 100 : 100} value={couponForm.amount} onChange={(e) => setCouponForm({ ...couponForm, amount: Number(e.target.value) })} className={c.inp} data-testid="grant-coupon-amount" />
                    <Input type="date" value={couponForm.expires} onChange={(e) => setCouponForm({ ...couponForm, expires: e.target.value })} className={c.inp} data-testid="grant-coupon-expires" />
                  </div>
                  <p className={`mt-1.5 text-[11px] ${c.txt40}`}>{d.coupon_everywhere}</p>
                  <Button onClick={createCoupon} disabled={busyAction || !couponForm.amount || !couponForm.expires} size="sm" className="mt-2 rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold h-8 text-xs" data-testid="grant-coupon-btn">
                    {d.coupon_create}
                  </Button>
                </div>
              )}
            </div>

            <div>
              <p className={`text-xs font-bold uppercase tracking-wider ${c.txt50} mb-2`}>{d.profile_notes}</p>
              <div className="flex gap-2">
                <Input value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder={d.note_placeholder} className={c.inp} data-testid="note-input" />
                <Button onClick={addNote} disabled={!newNote.trim()} className="rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold shrink-0" data-testid="note-add-btn">{d.note_add}</Button>
              </div>
              <div className="mt-3 space-y-2">
                {profile.notes.length === 0 && <p className={`text-sm ${c.txt40} italic`} data-testid="notes-empty">{d.note_empty}</p>}
                {profile.notes.map((n) => (
                  <div key={n.id} className={`rounded-xl border px-3 py-2.5 ${c.noteBox}`} data-testid={`note-${n.id}`}>
                    {editNote.id === n.id ? (
                      <div className="flex gap-2">
                        <Input value={editNote.text} onChange={(e) => setEditNote({ ...editNote, text: e.target.value })} className={c.inp} data-testid="note-edit-input" />
                        <Button onClick={saveNote} size="sm" className="rounded-full bg-brand text-brand-cream font-bold shrink-0" data-testid="note-save-btn">{d.note_save}</Button>
                      </div>
                    ) : (
                      <>
                        <p className={`text-sm text-brand-ink/90 whitespace-pre-wrap`}>{n.text}</p>
                        <div className="mt-1.5 flex items-center gap-3">
                          <span className={`text-[11px] ${c.txt40} font-mono`}>{fmtDateTime(n.created_at, lang)}</span>
                          <button type="button" onClick={() => setEditNote({ id: n.id, text: n.text })} className={`text-[11px] font-bold ${c.acc} hover:underline`} data-testid={`note-edit-${n.id}`}>{d.note_edit}</button>
                          <button type="button" onClick={() => delNote(n.id)} className="text-[11px] font-bold text-rose-500 hover:underline" data-testid={`note-del-${n.id}`}>{lang === "el" ? "Διαγραφή" : "Delete"}</button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <p className={`text-xs font-bold uppercase tracking-wider ${c.txt50}`}>{d.profile_history}</p>
                {isDev && (
                  <button type="button" onClick={() => setPastOpen(!pastOpen)} className={`inline-flex items-center gap-1 text-[11px] font-bold ${c.acc} hover:underline`} data-testid="past-booking-toggle">
                    <CalendarPlus className="h-3.5 w-3.5" /> {d.past_add}
                  </button>
                )}
              </div>
              {isDev && pastOpen && (
                <div className={`mb-3 rounded-xl border px-3.5 py-3 ${c.noteBox}`} data-testid="past-booking-form">
                  <p className={`text-xs font-bold ${c.txt70}`}>{d.past_title}</p>
                  <p className={`text-[11px] ${c.txt40} mt-0.5`}>{d.past_sub}</p>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <Select value={pastForm.type} onValueChange={(v) => setPastForm({ ...pastForm, type: v })}>
                      <SelectTrigger className={c.inp} data-testid="past-type"><SelectValue /></SelectTrigger>
                      <SelectContent className="z-[90]">
                        <SelectItem value="grooming">{d.grooming_type}</SelectItem>
                        <SelectItem value="selfwash">{d.selfwash_type}</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input type="date" max={new Date().toISOString().slice(0, 10)} value={pastForm.date} onChange={(e) => setPastForm({ ...pastForm, date: e.target.value })} className={c.inp} data-testid="past-date" />
                    <Input type="time" value={pastForm.time} onChange={(e) => setPastForm({ ...pastForm, time: e.target.value })} className={c.inp} data-testid="past-time" />
                    {pastForm.type === "grooming" ? (
                      <Input value={pastForm.service} onChange={(e) => setPastForm({ ...pastForm, service: e.target.value })} placeholder={d.past_service} className={c.inp} data-testid="past-service" />
                    ) : (
                      <Select value={pastForm.plan} onValueChange={(v) => setPastForm({ ...pastForm, plan: v })}>
                        <SelectTrigger className={c.inp} data-testid="past-plan"><SelectValue /></SelectTrigger>
                        <SelectContent className="z-[90]">
                          <SelectItem value="small">{d.size_small}</SelectItem>
                          <SelectItem value="medium">{d.size_medium}</SelectItem>
                          <SelectItem value="large">{d.size_large}</SelectItem>
                          <SelectItem value="giant">{d.size_giant}</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                    {pastForm.type === "selfwash" && (
                      <Input type="number" min="0" value={pastForm.price} onChange={(e) => setPastForm({ ...pastForm, price: e.target.value })} placeholder={d.past_price} className={c.inp} data-testid="past-price" />
                    )}
                    <Input value={pastForm.notes} onChange={(e) => setPastForm({ ...pastForm, notes: e.target.value })} placeholder={d.past_notes} className={`${c.inp} ${pastForm.type === "selfwash" ? "" : "col-span-2"}`} data-testid="past-notes" />
                  </div>
                  <Button onClick={addPast} disabled={busyAction || !pastForm.date} size="sm" className="mt-2 rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold h-8 text-xs" data-testid="past-save-btn">
                    {d.past_save}
                  </Button>
                </div>
              )}
              <div className="space-y-2" data-testid="profile-history">
                {profile.history.length === 0 && <p className={`text-sm ${c.txt40} italic`}>—</p>}
                {profile.history.map((hst) => (
                  <div key={`${hst.type}-${hst.id}`} className={`flex items-center gap-3 rounded-xl ${c.box} px-3 py-2.5 text-sm`} data-testid={`history-${hst.type}-${hst.id}`}>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ${hst.type === "grooming" ? c.chipG : c.chipS}`}>
                      {hst.type === "grooming" ? d.grooming_type : d.selfwash_type}
                    </span>
                    <div className="min-w-0">
                      <p className={`font-bold ${c.txt} font-mono text-[13px]`}>{hst.date} · {hst.time}</p>
                      <p className={`${c.txt50} text-xs truncate`}>{hst.detail}{hst.service ? ` · ${hst.service}` : ""}</p>
                    </div>
                    {hst.imported && (
                      <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 shrink-0" data-testid={`history-imported-${hst.id}`}>{d.past_badge}</span>
                    )}
                    <span className={`ml-auto text-xs ${c.txt60} shrink-0`}>{a.statuses[hst.status] || hst.status}{hst.price ? ` · €${hst.price}` : ""}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Dev: manual customer entry — no booking, no notification
const ManualCustomerDialog = ({ onClose, onSaved }) => {
  const { t } = useLang();
  const d = t.admin.dev;
  const c = crmTheme();
  const [form, setForm] = useState({ customer_name: "", phone: "", email: "", pet_name: "", pet_breed: "", pet_size: "", notes: "" });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!form.customer_name.trim() || !form.phone.trim()) return;
    setBusy(true);
    try {
      await api.post("/admin/customers/manual", {
        customer_name: form.customer_name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        pet_name: form.pet_name.trim() || null,
        pet_breed: form.pet_breed.trim() || null,
        pet_size: form.pet_size || null,
        notes: form.notes.trim() || null,
      });
      toast.success(d.cust_saved);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70]" data-testid="manual-customer-dialog">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-2rem)] max-w-md rounded-2xl border shadow-2xl p-5 max-h-[92vh] overflow-y-auto ${c.drawer}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 pr-1">
            <p className={`font-heading text-lg font-bold ${c.txt} flex items-center gap-2`}><UserPlus className={`h-5 w-5 ${c.acc}`} /> {d.cust_add}</p>
            <p className={`text-[11px] ${c.txt50} mt-0.5`}>{d.cust_add_sub}</p>
          </div>
          <button type="button" onClick={onClose} className={`grid place-items-center h-9 w-9 rounded-full shrink-0 ${c.closeBtn}`} data-testid="manual-customer-close" aria-label="Close">
            <XCircle className="h-5 w-5" />
          </button>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label className={`text-xs ${c.txt70}`}>{d.cust_name} *</Label>
            <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className={c.inp} data-testid="manual-cust-name" />
          </div>
          <div>
            <Label className={`text-xs ${c.txt70}`}>{d.cust_phone_l} *</Label>
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={c.inp} data-testid="manual-cust-phone" />
          </div>
          <div>
            <Label className={`text-xs ${c.txt70}`}>{d.cust_email_l}</Label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={c.inp} data-testid="manual-cust-email" />
          </div>
          <div>
            <Label className={`text-xs ${c.txt70}`}>{d.cust_pet}</Label>
            <Input value={form.pet_name} onChange={(e) => setForm({ ...form, pet_name: e.target.value })} className={c.inp} data-testid="manual-cust-pet" />
          </div>
          <div>
            <Label className={`text-xs ${c.txt70}`}>{d.cust_breed}</Label>
            <Input value={form.pet_breed} onChange={(e) => setForm({ ...form, pet_breed: e.target.value })} className={c.inp} data-testid="manual-cust-breed" />
          </div>
          <div className="col-span-2">
            <Label className={`text-xs ${c.txt70}`}>{d.cust_size}</Label>
            <Select value={form.pet_size} onValueChange={(v) => setForm({ ...form, pet_size: v })}>
              <SelectTrigger className={c.inp} data-testid="manual-cust-size"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent className="z-[90]">
                <SelectItem value="small">{d.size_small}</SelectItem>
                <SelectItem value="medium">{d.size_medium}</SelectItem>
                <SelectItem value="large">{d.size_large}</SelectItem>
                <SelectItem value="giant">{d.size_giant}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label className={`text-xs ${c.txt70}`}>{d.cust_note_l}</Label>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={c.inp} data-testid="manual-cust-notes" />
          </div>
        </div>
        <Button onClick={save} disabled={busy || !form.customer_name.trim() || !form.phone.trim()} className="mt-4 w-full rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold h-10" data-testid="manual-cust-save">
          {d.cust_save}
        </Button>
      </div>
    </div>
  );
};

export const DevCustomers = ({ canSend = true }) => {
  const { t } = useLang();
  const d = t.admin.dev;
  const c = crmTheme();
  const [customers, setCustomers] = useState([]);
  const [channel, setChannel] = useState("sms");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [profilePhone, setProfilePhone] = useState(null);
  const [addOpen, setAddOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/customers").then((r) => setCustomers(r.data)).catch(() => toast.error("Error"));
  }, []);
  useEffect(load, [load]);

  const q = search.trim().toLowerCase();
  const filtered = useMemo(
    () => customers.filter((cust) =>
      !q
      || (cust.name || "").toLowerCase().includes(q)
      || (cust.phone || "").includes(q)
      || (cust.email || "").toLowerCase().includes(q)
      || (cust.pets || []).some((p) => p.toLowerCase().includes(q))
      || (cust.breeds || []).some((b) => b.toLowerCase().includes(q))
    ),
    [customers, q]
  );

  const toggle = (phone) => setSelected((s) => { const n = new Set(s); n.has(phone) ? n.delete(phone) : n.add(phone); return n; });
  const selectAll = () => setSelected(new Set(filtered.map((cust) => cust.phone)));
  const selectNone = () => setSelected(new Set());

  const selectedCustomers = customers.filter((cust) => selected.has(cust.phone));
  const smsRecipients = selectedCustomers.length;
  const emailRecipients = selectedCustomers.filter((cust) => cust.email).length;
  const smsSegments = (txt) => {
    const n = txt.length + 12; // "\nCare For Me" suffix
    return n <= 70 ? 1 : Math.ceil((n - 70) / 67) + 1;
  };
  const estCredits = smsSegments(message) * smsRecipients;

  const sendBulk = async () => {
    if (!message.trim() || selected.size === 0) return;
    const n = channel === "sms" ? smsRecipients : emailRecipients;
    if (n === 0) { toast.error(d.no_recipients); return; }
    const confirmMsg = channel === "sms"
      ? d.bulk_confirm_sms.replace("{n}", n).replace("{c}", estCredits)
      : d.bulk_confirm_email.replace("{n}", n);
    if (!window.confirm(confirmMsg)) return;
    setSending(true);
    try {
      const res = await api.post("/admin/bulk-message", { channel, message: message.trim(), recipients: [...selected] });
      toast.success(d.bulk_done.replace("{s}", res.data.sent).replace("{t}", res.data.total));
      setMessage("");
      selectNone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="dev-customers">
      {canSend && (
        <div className={c.card}>
          <p className={`font-heading text-lg font-bold ${c.txt} flex items-center gap-2`}><Send className={`h-5 w-5 ${c.acc}`} /> {d.bulk_title}</p>
          <div className="mt-4 space-y-3">
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={d.bulk_message}
              className={c.textarea}
              data-testid="bulk-message-input"
            />
            <div className="flex flex-wrap items-center gap-4">
              <div className={`flex items-center gap-2 rounded-full border ${c.chanWrap} p-1`}>
                <button
                  type="button"
                  onClick={() => setChannel("sms")}
                  className={`rounded-full px-4 py-1.5 text-sm font-bold transition-colors ${channel === "sms" ? "bg-brand text-brand-cream" : c.chanOff}`}
                  data-testid="bulk-channel-sms"
                >
                  {d.bulk_channel_sms}
                </button>
                <button
                  type="button"
                  onClick={() => setChannel("email")}
                  className={`rounded-full px-4 py-1.5 text-sm font-bold transition-colors ${channel === "email" ? "bg-brand text-brand-cream" : c.chanOff}`}
                  data-testid="bulk-channel-email"
                >
                  {d.bulk_channel_email}
                </button>
              </div>
              <p className={`text-sm ${c.txt60}`} data-testid="bulk-recipients">
                {d.selected_recipients.replace("{n}", channel === "sms" ? smsRecipients : emailRecipients)}
                {channel === "sms" && message.trim() && smsRecipients > 0 && (
                  <span className={`ml-2 font-bold ${c.acc}`} data-testid="bulk-est-cost">· {d.bulk_est_cost}: ~{estCredits} {d.bulk_credits}</span>
                )}
              </p>
              <Button
                onClick={sendBulk}
                disabled={sending || !message.trim() || selected.size === 0}
                className="rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold ml-auto"
                data-testid="bulk-send-btn"
              >
                <Send className="h-4 w-4 mr-2" /> {sending ? d.bulk_sending : d.bulk_send}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className={c.card}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className={`font-heading text-lg font-bold ${c.txt} flex items-center gap-2`}><Users className={`h-5 w-5 ${c.acc}`} /> {d.customers_title}</p>
            <p className={`text-xs ${c.txt50}`}>{d.customers_sub}</p>
          </div>
          <div className="flex items-center gap-3">
            {canSend && (
              <Button onClick={() => setAddOpen(true)} size="sm" className="rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold h-9" data-testid="manual-customer-open">
                <UserPlus className="h-4 w-4 mr-1.5" /> {d.cust_add}
              </Button>
            )}
            {canSend && (
              <div className="flex items-center gap-2">
                <button type="button" onClick={selectAll} className={`text-xs font-bold ${c.acc} hover:underline`} data-testid="bulk-select-all">{d.select_all}</button>
                <span className="text-brand-ink/30">·</span>
                <button type="button" onClick={selectNone} className={`text-xs font-bold ${c.txt50} hover:underline`} data-testid="bulk-select-none">{d.select_none}</button>
              </div>
            )}
          </div>
        </div>
        <div className="mt-3">
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={d.search_customers} className={c.inp} data-testid="customer-search" />
        </div>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm" data-testid="customers-table">
            <thead>
              <tr className={`text-left text-xs uppercase tracking-wide ${c.txt50} border-b ${c.headBorder}`}>
                {canSend && <th className="py-2 pr-2 w-8"></th>}
                <th className="py-2 pr-4">{d.col_customer}</th>
                <th className="py-2 pr-4">Email</th>
                <th className="py-2 pr-4 text-center">{d.col_bookings}</th>
                <th className="py-2 pr-4 text-center">{d.col_revenue}</th>
                <th className="py-2">{d.col_last}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((cust) => (
                <tr key={cust.phone} className={`border-b cursor-pointer transition-colors ${c.row}`} data-testid={`customer-${cust.phone}`}>
                  {canSend && (
                    <td className="py-2.5 pr-2" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selected.has(cust.phone)}
                        onChange={() => toggle(cust.phone)}
                        className="h-4 w-4 accent-[#864D63] cursor-pointer"
                        data-testid={`customer-check-${cust.phone}`}
                      />
                    </td>
                  )}
                  <td className="py-2.5 pr-4" onClick={() => setProfilePhone(cust.phone)}>
                    <p className={`font-bold ${c.txt} flex items-center gap-2`}>
                      {cust.name || "—"}
                      {cust.has_account && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-bamboo-soft text-bamboo-deep text-[10px] font-bold px-2 py-0.5" data-testid={`customer-account-badge-${cust.phone}`}>
                          <UserCheck className="h-3 w-3" /> {d.account_badge}
                        </span>
                      )}
                    </p>
                    <a href={`tel:${cust.phone}`} onClick={(e) => e.stopPropagation()} className={`text-xs ${c.acc} font-semibold inline-flex items-center gap-1`}><Phone className="h-3 w-3" />{cust.phone}</a>
                    {((cust.pets || []).length > 0 || (cust.breeds || []).length > 0) && (
                      <p className={`text-[11px] ${c.txt50} mt-0.5`} data-testid={`customer-pets-${cust.phone}`}>
                        {[...(cust.pets || []), ...(cust.breeds || []).filter((b) => !(cust.pets || []).includes(b))].join(" · ")}
                      </p>
                    )}
                  </td>
                  <td className={`py-2.5 pr-4 ${c.txt70}`} onClick={() => setProfilePhone(cust.phone)}>{cust.email || "—"}</td>
                  <td className={`py-2.5 pr-4 text-center font-bold ${c.txt}`} onClick={() => setProfilePhone(cust.phone)}>{cust.bookings}</td>
                  <td className={`py-2.5 pr-4 text-center font-bold ${c.rev}`} onClick={() => setProfilePhone(cust.phone)}>€{cust.revenue}</td>
                  <td className={`py-2.5 ${c.txt60}`} onClick={() => setProfilePhone(cust.phone)}>{cust.last_visit || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && <p className={`text-center ${c.txt40} py-8`}>—</p>}
        </div>
      </div>

      {profilePhone && <CustomerProfileDrawer phone={profilePhone} onClose={() => setProfilePhone(null)} isDev={canSend} />}

      {addOpen && <ManualCustomerDialog onClose={() => setAddOpen(false)} onSaved={() => { setAddOpen(false); load(); }} />}

      {canSend && <DevAccountsList onOpen={(p) => setProfilePhone(p)} />}
    </div>
  );
};

// Registered customer accounts (dev) — list with stats, click opens the profile drawer
const DevAccountsList = ({ onOpen }) => {
  const { t, lang } = useLang();
  const d = t.admin.dev;
  const c = crmTheme();
  const [accounts, setAccounts] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    api.get("/admin/accounts").then((r) => setAccounts(r.data)).catch(() => setAccounts([]));
  }, []);

  if (accounts === null) return null;
  const q = search.trim().toLowerCase();
  const filtered = accounts.filter((x) =>
    !q
    || (x.first_name || "").toLowerCase().includes(q)
    || (x.last_name || "").toLowerCase().includes(q)
    || (x.email || "").toLowerCase().includes(q)
    || (x.phone || "").includes(q)
  );

  return (
    <div className={c.card} data-testid="dev-accounts">
      <p className={`font-heading text-lg font-bold ${c.txt} flex items-center gap-2`}><UserCheck className={`h-5 w-5 ${c.acc}`} /> {d.accounts_title}</p>
      <p className={`text-xs ${c.txt50}`}>{d.accounts_sub}</p>
      <div className="mt-3">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={d.search_customers} className={c.inp} data-testid="accounts-search" />
      </div>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm" data-testid="accounts-table">
          <thead>
            <tr className={`text-left text-xs uppercase tracking-wide ${c.txt50} border-b ${c.headBorder}`}>
              <th className="py-2 pr-4">{d.col_customer}</th>
              <th className="py-2 pr-4">Email</th>
              <th className="py-2 pr-4">{d.account_registered}</th>
              <th className="py-2 pr-4 text-center">{d.col_bookings}</th>
              <th className="py-2 pr-4 text-center">{d.orders_title}</th>
              <th className="py-2">{d.col_last}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((x) => (
              <tr key={x.id} className={`border-b cursor-pointer transition-colors ${c.row}`} onClick={() => x.phone && onOpen(x.phone)} data-testid={`account-row-${x.id}`}>
                <td className="py-2.5 pr-4">
                  <p className={`font-bold ${c.txt}`}>{x.first_name} {x.last_name || ""}</p>
                  {x.phone && <a href={`tel:${x.phone}`} onClick={(e) => e.stopPropagation()} className={`text-xs ${c.acc} font-semibold inline-flex items-center gap-1`}><Phone className="h-3 w-3" />{x.phone}</a>}
                </td>
                <td className={`py-2.5 pr-4 ${c.txt70}`}>{x.email}</td>
                <td className={`py-2.5 pr-4 ${c.txt60} font-mono text-xs`}>{x.created_at ? fmtDateTime(x.created_at, lang) : "—"}</td>
                <td className={`py-2.5 pr-4 text-center font-bold ${c.txt}`}>{x.bookings}</td>
                <td className={`py-2.5 pr-4 text-center font-bold ${c.txt}`}>{x.orders_count}{x.orders_total > 0 && <span className={`ml-1 ${c.rev}`}>€{x.orders_total}</span>}</td>
                <td className={`py-2.5 ${c.txt60}`}>{x.last_visit || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <p className={`text-center ${c.txt40} py-8`}>—</p>}
      </div>
    </div>
  );
};
// ---------------------------------------------------------------- Gallery management
export const DevGallery = () => {
  const { t } = useLang();
  const d = t.admin.dev;
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const dragId = useRef(null);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    api.get("/gallery").then((r) => setImages(r.data)).catch(() => toast.error("Error"));
  }, []);
  useEffect(load, [load]);

  const srcOf = (img) => (img.url.startsWith("http") ? img.url : `${API}/${img.url}`);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/admin/gallery", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(d.gallery_uploaded);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (id) => {
    if (!window.confirm(d.gallery_delete_confirm)) return;
    try {
      await api.delete(`/admin/gallery/${id}`);
      toast.success(d.gallery_deleted);
      setImages((p) => p.filter((i) => i.id !== id));
    } catch {
      toast.error("Error");
    }
  };

  const onDrop = async (targetId) => {
    const from = dragId.current;
    dragId.current = null;
    if (!from || from === targetId) return;
    const ids = images.map((i) => i.id);
    const next = ids.filter((x) => x !== from);
    next.splice(next.indexOf(targetId), 0, from);
    setImages(next.map((id) => images.find((i) => i.id === id)));
    try {
      await api.put("/admin/gallery/reorder", { ids: next });
    } catch {
      toast.error("Error");
      load();
    }
  };

  return (
    <div className={card} data-testid="dev-gallery">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <p className="font-heading text-lg font-bold text-brand-ink flex items-center gap-2"><Images className="h-5 w-5 text-brand" /> {d.gallery_title}</p>
          <p className="text-xs text-brand-ink/50">{d.gallery_sub}</p>
        </div>
        <div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="gallery-file-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold" data-testid="gallery-upload-btn">
            <Upload className="h-4 w-4 mr-2" /> {uploading ? d.gallery_uploading : d.gallery_upload}
          </Button>
        </div>
      </div>
      {images.length === 0 ? (
        <p className="text-center text-brand-ink/50 py-10">{d.gallery_empty}</p>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3" data-testid="dev-gallery-grid">
          {images.map((img) => (
            <div
              key={img.id}
              draggable
              onDragStart={() => { dragId.current = img.id; }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(img.id)}
              className="relative group rounded-xl overflow-hidden border border-brand/10 cursor-grab active:cursor-grabbing"
              data-testid={`dev-gallery-img-${img.id}`}
            >
              <img src={srcOf(img)} alt="" className="w-full h-24 object-cover" draggable={false} />
              <button
                type="button"
                onClick={() => remove(img.id)}
                className="absolute top-1 right-1 grid place-items-center h-7 w-7 rounded-full bg-rose-500/90 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`dev-gallery-del-${img.id}`}
                aria-label="Delete"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------- Messaging (SMS settings + histories)
export const DevMessaging = () => {
  const { t, lang } = useLang();
  const a = t.admin;
  const [smsSettings, setSmsSettings] = useState({ enabled: false });
  const [smsTestPhone, setSmsTestPhone] = useState("");
  const [smsTesting, setSmsTesting] = useState(false);
  const [smsHistoryOpen, setSmsHistoryOpen] = useState(false);
  const [emailHistoryOpen, setEmailHistoryOpen] = useState(false);
  const [balance, setBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/sms/settings").then((r) => setSmsSettings(r.data)).catch(() => {});
  }, []);

  const loadBalance = useCallback(() => {
    setBalanceLoading(true);
    api.get("/admin/sms/balance")
      .then((r) => setBalance(r.data.balance))
      .catch(() => setBalance(null))
      .finally(() => setBalanceLoading(false));
  }, []);

  useEffect(() => { load(); loadBalance(); }, [load, loadBalance]);

  const toggleSms = async (v) => {
    try {
      await api.post("/admin/sms/toggle", { enabled: v });
      setSmsSettings((s) => ({ ...s, enabled: v }));
      toast.success(v ? a.sms.toggled_on : a.sms.toggled_off);
    } catch {
      toast.error(a.sms.toggle_error);
    }
  };

  const sendTestSms = async () => {
    if (!smsTestPhone.trim()) return;
    setSmsTesting(true);
    try {
      await api.post("/admin/sms/test", { phone: smsTestPhone.trim() });
      toast.success(a.sms.test_success);
    } catch (err) {
      toast.error(err.response?.data?.detail || a.sms.send_error);
    } finally {
      setSmsTesting(false);
      load();
    }
  };

  return (
    <div className="space-y-5" data-testid="dev-messaging">
      <div className={card}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="grid place-items-center h-10 w-10 rounded-full bg-brand-soft/50 text-brand"><MessageSquare className="h-5 w-5" /></span>
            <div>
              <p className="font-heading text-lg font-bold text-brand-ink">{a.sms.title}</p>
              <p className="text-xs text-brand-ink/60">{a.sms.subtitle}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${smsSettings.enabled ? "bg-bamboo-soft text-bamboo-deep" : "bg-brand-soft/50 text-brand-ink/50"}`} data-testid="sms-status-chip">
              {smsSettings.enabled ? a.sms.on : a.sms.off}
            </span>
            <Switch checked={!!smsSettings.enabled} onCheckedChange={toggleSms} data-testid="sms-toggle" />
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Input value={smsTestPhone} onChange={(e) => setSmsTestPhone(e.target.value)} placeholder={a.sms.test_placeholder} inputMode="tel" className={`max-w-[200px] ${inputCls}`} data-testid="sms-test-phone" />
          <Button onClick={sendTestSms} disabled={smsTesting || !smsTestPhone.trim()} variant="outline" className="rounded-full border-brand/30 text-brand hover:bg-brand-soft/50 font-bold h-10" data-testid="sms-test-send">
            <Send className="h-4 w-4 mr-2" /> {smsTesting ? a.sms.test_sending : a.sms.test_send}
          </Button>
          <p className="text-xs text-brand-ink/50" data-testid="sms-last-attempt">
            {a.sms.last_attempt}: {smsSettings.last_attempt_at
              ? `${new Date(smsSettings.last_attempt_at).toLocaleString(lang === "el" ? "el-GR" : "en-GB", { dateStyle: "short", timeStyle: "short" })} · ${smsSettings.last_attempt_phone || ""} · ${smsSettings.last_attempt_result || ""}`
              : a.sms.never}
          </p>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-soft/50 text-brand text-xs font-bold px-3 py-1.5" data-testid="sms-balance">
            <Wallet className="h-3.5 w-3.5" />
            {a.sms.balance}: {balance === null ? "—" : balance}
            <button type="button" onClick={loadBalance} className="ml-1 hover:text-brand-ink" data-testid="sms-balance-refresh" aria-label="Refresh">
              <RefreshCw className={`h-3 w-3 ${balanceLoading ? "animate-spin" : ""}`} />
            </button>
          </span>
        </div>
      </div>

      <div className={card}>
        <p className="font-heading text-lg font-bold text-brand-ink mb-3">{t.admin.tab_messaging} — {lang === "el" ? "Ιστορικά" : "History"}</p>
        <div className="flex flex-wrap gap-3">
          <Button onClick={() => setSmsHistoryOpen(true)} variant="outline" className="rounded-full border-brand/30 text-brand hover:bg-brand-soft/50 font-bold" data-testid="sms-history-btn">
            <MessageSquare className="h-4 w-4 mr-1.5" /> {a.sms.history_button}
          </Button>
          <Button onClick={() => setEmailHistoryOpen(true)} variant="outline" className="rounded-full border-brand/30 text-brand hover:bg-brand-soft/50 font-bold" data-testid="email-history-btn">
            <Mail className="h-4 w-4 mr-1.5" /> {a.email_history.button}
          </Button>
        </div>
      </div>

      <SmsHistoryDialog open={smsHistoryOpen} onOpenChange={setSmsHistoryOpen} />
      <EmailHistoryDialog open={emailHistoryOpen} onOpenChange={setEmailHistoryOpen} />
    </div>
  );
};

// ---------------------------------------------------------------- Settings (closures, prices, password, health, links, slots, hours)
const minToStr = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
const strToMin = (s) => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(s || "");
  return m ? parseInt(m[1], 10) * 60 + parseInt(m[2], 10) : null;
};

export const DevSettings = () => {
  const { t } = useLang();
  const d = t.admin.dev;
  const [closures, setClosures] = useState([]);
  const [newRange, setNewRange] = useState({ start: "", end: "", reason: "", show_popup: false, popup_text: "", icon: "sun" });
  const [singleDay, setSingleDay] = useState(false);
  const [prices, setPrices] = useState(null);
  const [pw, setPw] = useState({ current: "", next: "" });
  const [health, setHealth] = useState(null);
  const [weekly, setWeekly] = useState(null);
  const [hours, setHours] = useState(null);

  useEffect(() => {
    api.get("/store/closures").then((r) => setClosures(r.data.closures || [])).catch(() => {});
    loadPlanPrices(true).then(setPrices);
    api.get("/admin/health").then((r) => setHealth(r.data.checks)).catch(() => {});
    api.get("/admin/grooming-weekly").then((r) => {
      const s = r.data.slots;
      const asText = {};
      for (const k of Object.keys(s)) asText[k] = (s[k] || []).join(", ");
      setWeekly(asText);
    }).catch(() => {});
    api.get("/admin/opening-hours").then((r) => {
      const days = {};
      for (const k of Object.keys(r.data.days)) {
        const wins = r.data.days[k].map((w) => ({ from: minToStr(w[0]), to: minToStr(w[1]) }));
        while (wins.length < 2) wins.push({ from: "", to: "" });
        days[k] = wins;
      }
      setHours(days);
    }).catch(() => {});
  }, []);

  const saveClosures = async () => {
    try {
      await api.put("/admin/closures", { ranges: closures });
      toast.success(d.closures_saved);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const addRange = () => {
    if (!newRange.start || (!singleDay && !newRange.end)) return;
    setClosures((c) => [...c, { ...newRange, end: singleDay ? newRange.start : newRange.end }]);
    setNewRange({ start: "", end: "", reason: "", show_popup: false, popup_text: "", icon: "sun" });
  };

  const savePrices = async () => {
    try {
      await api.put("/admin/plan-prices", { prices });
      toast.success(d.prices_saved);
      loadPlanPrices(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const saveWeekly = async () => {
    const slots = {};
    for (const k of Object.keys(weekly)) {
      slots[k] = weekly[k].split(",").map((x) => x.trim()).filter(Boolean);
    }
    try {
      await api.put("/admin/grooming-weekly", { slots });
      toast.success(d.weekly_saved);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const saveHours = async () => {
    const days = {};
    for (const k of Object.keys(hours)) {
      const windows = [];
      for (const w of hours[k]) {
        const frm = strToMin(w.from);
        const to = strToMin(w.to);
        if (w.from && w.to && frm !== null && to !== null) windows.push([frm, to]);
      }
      days[k] = windows;
    }
    try {
      await api.put("/admin/opening-hours", { days });
      toast.success(d.hours_saved);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const changePw = async () => {
    try {
      await api.post("/admin/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success(d.password_saved);
      setPw({ current: "", next: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const planLabels = { small: "Small", medium: "Medium", large: "Large", giant: "Giant" };
  const hasWarn = health?.some((c) => !c.ok);
  const dayKeys = ["0", "1", "2", "3", "4", "5", "6"];

  return (
    <div className="space-y-5" data-testid="dev-settings">
      <div className={card} data-testid="dev-health">
        <p className="font-heading text-lg font-bold text-brand-ink flex items-center gap-2 mb-3">
          {hasWarn ? <AlertTriangle className="h-5 w-5 text-amber-500" /> : <CheckCircle2 className="h-5 w-5 text-bamboo-deep" />} {d.health_title}
        </p>
        {!health ? <p className="text-sm text-brand-ink/50">...</p> : (
          <div className="space-y-2" data-testid="health-list">
            {health.map((c) => (
              <div key={c.key} className="flex items-center gap-2 text-sm" data-testid={`health-${c.key}`}>
                {c.ok ? <CheckCircle2 className="h-4 w-4 text-bamboo-deep shrink-0" /> : <XCircle className="h-4 w-4 text-amber-500 shrink-0" />}
                <span className="text-brand-ink/80">{c.detail}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={card} data-testid="dev-closures">
        <p className="font-heading text-lg font-bold text-brand-ink flex items-center gap-2 mb-3"><CalendarX2 className="h-5 w-5 text-brand" /> {d.closures_title}</p>
        {closures.length === 0 ? (
          <p className="text-sm text-brand-ink/50 mb-3" data-testid="closures-empty">{d.closures_empty}</p>
        ) : (
          <div className="space-y-2 mb-3" data-testid="closures-list">
            {closures.map((r, i) => (
              <div key={i} className="flex items-center gap-2 text-sm rounded-xl bg-stone-alt/50 px-3 py-2">
                <span className="font-bold text-brand-ink">{r.start} → {r.end}</span>
                {r.reason && <span className="text-brand-ink/50">({r.reason})</span>}
                <button type="button" onClick={() => setClosures((c) => c.filter((_, j) => j !== i))} className="ml-auto text-rose-500 hover:text-rose-600" data-testid={`closure-del-${i}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="space-y-3">
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex items-center gap-2 text-sm font-semibold text-brand-ink cursor-pointer self-end pb-2">
              <Switch checked={singleDay} onCheckedChange={setSingleDay} data-testid="closure-single-day" />
              {d.closures_single_day}
            </label>
            <div><Label className="text-xs">{singleDay ? d.closures_date : d.closures_from}</Label><Input type="date" value={newRange.start} onChange={(e) => setNewRange({ ...newRange, start: e.target.value })} className={inputCls} data-testid="closure-start" /></div>
            {!singleDay && (
              <div><Label className="text-xs">{d.closures_to}</Label><Input type="date" value={newRange.end} onChange={(e) => setNewRange({ ...newRange, end: e.target.value })} className={inputCls} data-testid="closure-end" /></div>
            )}
            <div><Label className="text-xs">{d.closures_reason}</Label><Input value={newRange.reason} onChange={(e) => setNewRange({ ...newRange, reason: e.target.value })} className={inputCls} data-testid="closure-reason" /></div>
            <Button onClick={addRange} variant="outline" className="rounded-full border-brand/30 text-brand font-bold" data-testid="closure-add">{d.closures_add}</Button>
          </div>
          <div className="flex flex-wrap items-center gap-3 rounded-xl bg-stone-alt/50 px-3 py-3">
            <label className="flex items-center gap-2 text-sm font-semibold text-brand-ink cursor-pointer">
              <Switch checked={newRange.show_popup} onCheckedChange={(v) => setNewRange({ ...newRange, show_popup: v })} data-testid="closure-popup-switch" />
              {d.closures_popup}
            </label>
            {newRange.show_popup && (
              <>
                <Input
                  value={newRange.popup_text}
                  onChange={(e) => setNewRange({ ...newRange, popup_text: e.target.value })}
                  placeholder={d.closures_popup_text}
                  className={`${inputCls} flex-1 min-w-[220px]`}
                  data-testid="closure-popup-text"
                />
                <div className="flex items-center gap-1.5" data-testid="closure-icon-picker">
                  {CLOSURE_ICON_KEYS.map((k) => {
                    const Icon = CLOSURE_ICONS[k];
                    return (
                      <button
                        key={k}
                        type="button"
                        onClick={() => setNewRange({ ...newRange, icon: k })}
                        className={`grid place-items-center h-9 w-9 rounded-full border transition-colors ${newRange.icon === k ? "bg-brand text-brand-cream border-brand" : "border-brand/20 text-brand-ink/60 hover:bg-brand-soft/50"}`}
                        data-testid={`closure-icon-${k}`}
                        aria-label={k}
                      >
                        <Icon className="h-4 w-4" />
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
          <Button onClick={saveClosures} className="rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold" data-testid="closures-save">{d.closures_save}</Button>
        </div>
      </div>

      <div className={card} data-testid="dev-weekly-slots">
        <p className="font-heading text-lg font-bold text-brand-ink flex items-center gap-2 mb-1"><Clock3 className="h-5 w-5 text-brand" /> {d.weekly_title}</p>
        <p className="text-xs text-brand-ink/50 mb-3">{d.weekly_sub}</p>
        {weekly && (
          <div className="space-y-2" data-testid="weekly-editor">
            {dayKeys.map((k, i) => (
              <div key={k} className="flex items-center gap-3">
                <span className="w-24 text-sm font-bold text-brand-ink">{d.day_names[i]}</span>
                <Input
                  value={weekly[k] instanceof Array ? weekly[k].join(", ") : weekly[k]}
                  onChange={(e) => setWeekly({ ...weekly, [k]: e.target.value })}
                  placeholder={d.weekly_placeholder}
                  className={`${inputCls} font-mono`}
                  data-testid={`weekly-day-${k}`}
                />
              </div>
            ))}
            <Button onClick={saveWeekly} className="rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold mt-2" data-testid="weekly-save">{d.weekly_save}</Button>
          </div>
        )}
      </div>

      <div className={card} data-testid="dev-opening-hours">
        <p className="font-heading text-lg font-bold text-brand-ink flex items-center gap-2 mb-1"><Clock className="h-5 w-5 text-brand" /> {d.hours_title}</p>
        <p className="text-xs text-brand-ink/50 mb-3">{d.hours_sub}</p>
        {hours && (
          <div className="space-y-2" data-testid="hours-editor">
            {dayKeys.map((k, i) => (
              <div key={k} className="flex flex-wrap items-center gap-2">
                <span className="w-24 text-sm font-bold text-brand-ink">{d.day_names[i]}</span>
                {[0, 1].map((w) => (
                  <span key={w} className="flex items-center gap-1">
                    <Input
                      value={hours[k]?.[w]?.from ?? ""}
                      onChange={(e) => {
                        const next = { ...hours };
                        const arr = [...(next[k] || [{ from: "", to: "" }, { from: "", to: "" }])];
                        arr[w] = { ...arr[w], from: e.target.value };
                        next[k] = arr;
                        setHours(next);
                      }}
                      placeholder="12:00"
                      className={`${inputCls} w-20 font-mono`}
                      data-testid={`hours-${k}-${w}-from`}
                    />
                    <span className="text-brand-ink/40">–</span>
                    <Input
                      value={hours[k]?.[w]?.to ?? ""}
                      onChange={(e) => {
                        const next = { ...hours };
                        const arr = [...(next[k] || [{ from: "", to: "" }, { from: "", to: "" }])];
                        arr[w] = { ...arr[w], to: e.target.value };
                        next[k] = arr;
                        setHours(next);
                      }}
                      placeholder="14:30"
                      className={`${inputCls} w-20 font-mono`}
                      data-testid={`hours-${k}-${w}-to`}
                    />
                  </span>
                ))}
              </div>
            ))}
            <Button onClick={saveHours} className="rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold mt-2" data-testid="hours-save">{d.hours_save}</Button>
          </div>
        )}
      </div>

      <div className={card} data-testid="dev-prices">
        <p className="font-heading text-lg font-bold text-brand-ink flex items-center gap-2 mb-3"><Euro className="h-5 w-5 text-brand" /> {d.prices_title}</p>
        {prices && (
          <div className="flex flex-wrap items-end gap-3">
            {Object.keys(planLabels).map((k) => (
              <div key={k}>
                <Label className="text-xs">{planLabels[k]}</Label>
                <Input type="number" min="0" value={prices[k]} onChange={(e) => setPrices({ ...prices, [k]: e.target.value })} className={`${inputCls} w-24`} data-testid={`price-${k}`} />
              </div>
            ))}
            <Button onClick={savePrices} className="rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold" data-testid="prices-save">{d.prices_save}</Button>
          </div>
        )}
      </div>

      <div className={card} data-testid="dev-password">
        <p className="font-heading text-lg font-bold text-brand-ink flex items-center gap-2 mb-3"><KeyRound className="h-5 w-5 text-brand" /> {d.password_title}</p>
        <div className="flex flex-wrap items-end gap-3">
          <div><Label className="text-xs">{d.password_current}</Label><Input type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} className={inputCls} data-testid="pw-current" /></div>
          <div><Label className="text-xs">{d.password_new}</Label><Input type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} className={inputCls} data-testid="pw-new" /></div>
          <Button onClick={changePw} disabled={!pw.current || pw.next.length < 6} className="rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold" data-testid="pw-save">{d.password_save}</Button>
        </div>
      </div>

      <div className={card} data-testid="dev-links">
        <p className="font-heading text-lg font-bold text-brand-ink flex items-center gap-2 mb-3"><ExternalLink className="h-5 w-5 text-brand" /> {d.links_title}</p>
        <div className="flex flex-wrap gap-3">
          <a href="https://resend.com/emails" target="_blank" rel="noreferrer"><Button variant="outline" className="rounded-full border-brand/30 text-brand font-bold no-a11y-underline" data-testid="link-resend">Resend</Button></a>
          <a href="https://www.smsbox.gr" target="_blank" rel="noreferrer"><Button variant="outline" className="rounded-full border-brand/30 text-brand font-bold no-a11y-underline" data-testid="link-smsbox">SMSBox</Button></a>
          <a href="https://analytics.google.com" target="_blank" rel="noreferrer"><Button variant="outline" className="rounded-full border-brand/30 text-brand font-bold no-a11y-underline" data-testid="link-ga">Google Analytics</Button></a>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------- All bookings (read-only)
export const DevAllBookings = ({ grooming, selfwash }) => {
  const { t } = useLang();
  const d = t.admin.dev;
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const rows = useMemo(() => {
    const g = grooming.map((b) => ({ ...b, _type: "grooming", _dt: b.preferred_date, _tm: b.preferred_time }));
    const s = selfwash.map((b) => ({ ...b, _type: "selfwash", _dt: b.date, _tm: b.time }));
    return [...g, ...s]
      .filter((b) => (typeFilter === "all" || b._type === typeFilter) && (statusFilter === "all" || b.status === statusFilter))
      .sort((a, b2) => (b2._dt + (b2._tm || "")).localeCompare(a._dt + (a._tm || "")));
  }, [grooming, selfwash, typeFilter, statusFilter]);

  return (
    <div className={card} data-testid="dev-all-bookings">
      <div className="flex flex-wrap gap-3 mb-4">
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className={`w-40 ${inputCls}`} data-testid="all-filter-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{d.filter_all}</SelectItem>
            <SelectItem value="grooming">{d.grooming_type}</SelectItem>
            <SelectItem value="selfwash">{d.selfwash_type}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className={`w-40 ${inputCls}`} data-testid="all-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{d.filter_all}</SelectItem>
            {["confirmed", "done", "cancelled", "no_show"].map((s) => (
              <SelectItem key={s} value={s}>{t.admin.statuses[s] || s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="all-bookings-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-brand-ink/50 border-b border-brand/10">
              <th className="py-2 pr-3">{d.filter_type}</th>
              <th className="py-2 pr-3">{d.col_customer}</th>
              <th className="py-2 pr-3">{t.admin.dev.col_last.split(" ")[0]}</th>
              <th className="py-2 pr-3">{d.filter_status}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((b) => (
              <tr key={`${b._type}-${b.id}`} className="border-b border-brand/5" data-testid={`all-${b._type}-${b.id}`}>
                <td className="py-2 pr-3">
                  <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${b._type === "grooming" ? "bg-brand-soft text-brand" : "bg-bamboo-soft text-bamboo-deep"}`}>
                    {b._type === "grooming" ? d.grooming_type : d.selfwash_type}
                  </span>
                </td>
                <td className="py-2 pr-3"><span className="font-bold text-brand-ink">{b.customer_name}</span> <span className="text-brand-ink/50 text-xs">{b.phone}</span></td>
                <td className="py-2 pr-3 text-brand-ink/70">{b._dt} {b._tm}</td>
                <td className="py-2 pr-3 text-brand-ink/70">{t.admin.statuses[b.status] || b.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <p className="text-center text-brand-ink/50 py-8">—</p>}
      </div>
    </div>
  );
};


// ---------------------------------------------------------------- SMS templates
const SAMPLE_VARS = { "{name}": "Γιώργος", "{date}": "25/10", "{time}": "11:00", "{link}": "www.careforme.gr/?m=6912345678", "{order}": "B-0042" };
const REVIEW_LINK = "https://g.page/r/CVdlBrhCOdaqEBE/review";

const renderPreview = (tpl, key) => {
  let out = tpl || "";
  const vars = { ...SAMPLE_VARS, "{link}": key === "sms_review" ? REVIEW_LINK : SAMPLE_VARS["{link}"] };
  for (const [k, v] of Object.entries(vars)) out = out.replaceAll(k, v);
  return out + "\nCare For Me";
};

export const DevTemplates = () => {
  const { t } = useLang();
  const d = t.admin.dev;
  const [tpls, setTpls] = useState(null);
  const [saving, setSaving] = useState(false);
  const [defaults, setDefaults] = useState({});

  const load = useCallback(() => {
    api.get("/admin/templates").then((r) => { setTpls(r.data.templates); setDefaults(r.data.defaults); }).catch(() => toast.error("Error"));
  }, []);
  useEffect(load, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/templates", { templates: tpls });
      toast.success(d.tpl_saved);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    try {
      await api.put("/admin/templates", { templates: {} });
      toast.success(d.tpl_reset_done);
      load();
    } catch { toast.error("Error"); }
  };

  const FIELDS = [
    { key: "sms_confirmation", label: d.tpl_confirmation, testid: "tpl-confirmation" },
    { key: "sms_edit", label: d.tpl_edit, testid: "tpl-edit" },
    { key: "sms_reminder", label: d.tpl_reminder, testid: "tpl-reminder" },
    { key: "sms_review", label: d.tpl_review, testid: "tpl-review" },
    { key: "sms_winback", label: d.tpl_winback, testid: "tpl-winback" },
    { key: "sms_order_confirmation", label: d.tpl_order_confirmation, testid: "tpl-order-confirmation" },
    { key: "sms_order_ready", label: d.tpl_order_ready, testid: "tpl-order-ready" },
  ];

  return (
    <div className="space-y-5" data-testid="dev-templates">
      <div className={card}>
        <p className="font-heading text-lg font-bold text-brand-ink flex items-center gap-2"><FileText className="h-5 w-5 text-brand" /> {d.templates_title}</p>
        <p className="text-xs text-brand-ink/50 mt-1 leading-relaxed">{d.templates_sub}</p>
      </div>
      {!tpls ? <p className="text-brand-ink/50 text-center py-10">...</p> : (
        <>
          {FIELDS.map((f) => (
            <div className={card} key={f.key}>
              <p className="font-bold text-brand-ink mb-2">{f.label}</p>
              <Textarea
                value={tpls[f.key] || ""}
                onChange={(e) => setTpls({ ...tpls, [f.key]: e.target.value })}
                className="rounded-xl border-brand/20 bg-stone-alt/50 text-brand-ink placeholder:text-brand-ink/40 min-h-[70px] font-mono text-sm"
                data-testid={f.testid}
              />
              <div className="mt-2 rounded-xl bg-stone-alt/60 border border-brand/5 px-3 py-2" data-testid={`${f.testid}-preview`}>
                <p className="text-[10px] uppercase tracking-wider text-brand-ink/40 mb-1">{d.tpl_preview}</p>
                <p className="text-sm text-brand-ink/80 whitespace-pre-wrap font-mono">{renderPreview(tpls[f.key], f.key)}</p>
              </div>
            </div>
          ))}
          <div className="flex flex-wrap gap-3">
            <Button onClick={save} disabled={saving} className="rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold" data-testid="tpl-save">
              {saving ? t.admin.saving : d.tpl_save}
            </Button>
            <Button onClick={reset} variant="outline" className="rounded-full border-brand/30 text-brand-ink/70 hover:bg-brand-soft/50 font-bold" data-testid="tpl-reset">
              <RotateCcw className="h-4 w-4 mr-2" /> {d.tpl_reset}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------- System (health + balance + recent sends + export)
export const DevSystem = () => {
  const { t, lang } = useLang();
  const d = t.admin.dev;
  const a = t.admin;
  const [health, setHealth] = useState(null);
  const [balance, setBalance] = useState(null);
  const [logs, setLogs] = useState([]);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    api.get("/admin/health").then((r) => setHealth(r.data.checks)).catch(() => {});
    api.get("/admin/sms/balance").then((r) => setBalance(r.data.balance)).catch(() => setBalance(null));
    api.get("/admin/sms/logs").then((r) => setLogs(r.data.slice(0, 15))).catch(() => {});
  }, []);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/export/bookings.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = "careforme-bookings.csv";
      link.click();
      URL.revokeObjectURL(url);
      toast.success(d.export_done);
    } catch { toast.error("Error"); } finally { setExporting(false); }
  };

  return (
    <div className="space-y-5" data-testid="dev-system">
      <div className={card}>
        <p className="font-heading text-lg font-bold text-brand-ink flex items-center gap-2 mb-3"><Activity className="h-5 w-5 text-brand" /> {d.system_title}</p>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-soft/50 text-brand text-xs font-bold px-3 py-1.5" data-testid="system-sms-balance">
            <Wallet className="h-3.5 w-3.5" /> {a.sms.balance}: {balance === null ? "—" : balance}
          </span>
          <Button onClick={exportCsv} disabled={exporting} variant="outline" className="rounded-full border-brand/30 text-brand-ink hover:bg-brand-soft/50 font-bold" data-testid="export-csv-btn">
            <Download className="h-4 w-4 mr-2" /> {exporting ? "..." : d.export_csv}
          </Button>
        </div>
        {!health ? <p className="text-sm text-brand-ink/50">...</p> : (
          <div className="space-y-2" data-testid="system-health-list">
            {health.map((c) => (
              <div key={c.key} className="flex items-center gap-2 text-sm" data-testid={`system-health-${c.key}`}>
                {c.ok ? <CheckCircle2 className="h-4 w-4 text-bamboo-deep shrink-0" /> : <XCircle className="h-4 w-4 text-amber-500 shrink-0" />}
                <span className="text-brand-ink/80">{c.detail}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={card}>
        <p className="font-heading text-lg font-bold text-brand-ink flex items-center gap-2 mb-1"><MessageSquare className="h-5 w-5 text-brand" /> {d.system_sms_log}</p>
        <p className="text-xs text-brand-ink/50 mb-3">{d.system_delivery_note}</p>
        {logs.length === 0 ? <p className="text-sm text-brand-ink/40 italic" data-testid="system-no-logs">{d.system_no_logs}</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="system-sms-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-brand-ink/50 border-b border-brand/10">
                  <th className="py-2 pr-3">—</th>
                  <th className="py-2 pr-3">{lang === "el" ? "Τηλέφωνο" : "Phone"}</th>
                  <th className="py-2 pr-3">{lang === "el" ? "Τύπος" : "Type"}</th>
                  <th className="py-2">{lang === "el" ? "Ώρα" : "Time"}</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id} className="border-b border-brand/5" data-testid={`system-sms-${l.id}`}>
                    <td className="py-2 pr-3">{l.ok ? <CheckCircle2 className="h-4 w-4 text-bamboo-deep" /> : <XCircle className="h-4 w-4 text-rose-500" />}</td>
                    <td className="py-2 pr-3 font-mono text-brand-ink/80">{l.phone}</td>
                    <td className="py-2 pr-3 text-brand-ink/60">{l.kind}</td>
                    <td className="py-2 font-mono text-brand-ink/50 text-xs">{fmtDateTime(l.created_at, lang)}{!l.ok && l.detail ? ` · ${l.detail}` : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------- Announcements (dev → shop popup)
export const DevAnnouncements = () => {
  const { t, lang } = useLang();
  const d = t.admin.dev;
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", body: "", icon: "megaphone" });
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    api.get("/admin/announcements").then((r) => setItems(r.data)).catch(() => toast.error("Error"));
  }, []);
  useEffect(load, [load]);

  const submit = async () => {
    if (!form.title.trim() || !form.body.trim()) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title.trim());
      fd.append("body", form.body.trim());
      fd.append("icon", form.icon);
      if (file) fd.append("file", file);
      await api.post("/admin/announcements", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(d.ann_sent);
      setForm({ title: "", body: "", icon: "megaphone" });
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setSending(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm(d.ann_delete_confirm)) return;
    try { await api.delete(`/admin/announcements/${id}`); setItems((p) => p.filter((x) => x.id !== id)); }
    catch { toast.error("Error"); }
  };

  return (
    <div className="space-y-5" data-testid="dev-announcements">
      <div className={card}>
        <p className="font-heading text-lg font-bold text-brand-ink flex items-center gap-2"><Megaphone className="h-5 w-5 text-brand" /> {d.ann_form_title}</p>
        <p className="text-xs text-brand-ink/50 mt-1">{d.ann_form_sub}</p>
        <div className="mt-4 space-y-3">
          <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={d.ann_title} className={inputCls} data-testid="ann-title" />
          <Textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder={d.ann_body} className="rounded-xl border-brand/20 bg-stone-alt/50 text-brand-ink placeholder:text-brand-ink/40 min-h-[90px]" data-testid="ann-body" />
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-brand-ink/50 mb-2">{d.ann_icon}</p>
            <div className="flex flex-wrap items-center gap-1.5" data-testid="ann-icon-picker">
              {ANNOUNCEMENT_ICON_KEYS.map((k) => {
                const Icon = ANNOUNCEMENT_ICONS[k];
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setForm({ ...form, icon: k })}
                    className={`grid place-items-center h-9 w-9 rounded-full border transition-colors ${form.icon === k ? "bg-brand text-brand-cream border-brand" : "border-brand/20 text-brand-ink/60 hover:bg-brand-soft/50"}`}
                    data-testid={`ann-icon-${k}`}
                    aria-label={k}
                  >
                    <Icon className="h-4 w-4" />
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <input ref={fileRef} type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="ann-file-input" />
            <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} className="rounded-full border-brand/30 text-brand-ink/80 hover:bg-brand-soft/50 font-bold" data-testid="ann-file-btn">
              <Paperclip className="h-4 w-4 mr-2" /> {d.ann_attachment_pick}
            </Button>
            {file && <span className="text-xs text-brand-ink/60 font-mono" data-testid="ann-file-name">{file.name}</span>}
          </div>
          <Button onClick={submit} disabled={sending || !form.title.trim() || !form.body.trim()} className="rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold" data-testid="ann-send">
            <Megaphone className="h-4 w-4 mr-2" /> {sending ? d.ann_sending : d.ann_send}
          </Button>
        </div>
      </div>

      <div className={card}>
        <p className="font-heading text-lg font-bold text-brand-ink mb-3">{d.ann_list}</p>
        {items.length === 0 ? (
          <p className="text-sm text-brand-ink/40 italic" data-testid="ann-empty">{d.ann_empty}</p>
        ) : (
          <div className="space-y-2" data-testid="ann-list">
            {items.map((ann) => {
              const Icon = ANNOUNCEMENT_ICONS[ann.icon] || Megaphone;
              return (
                <div key={ann.id} className="flex items-start gap-3 rounded-xl bg-stone-alt/50 border border-brand/10 px-3 py-2.5" data-testid={`ann-item-${ann.id}`}>
                  <span className="grid place-items-center h-9 w-9 rounded-full bg-brand-soft text-brand shrink-0"><Icon className="h-4 w-4" /></span>
                  <div className="min-w-0">
                    <p className="font-bold text-brand-ink">{ann.title}</p>
                    <p className="text-sm text-brand-ink/60 whitespace-pre-wrap">{ann.body}</p>
                    <p className="mt-1 text-[11px] text-brand-ink/40 font-mono">
                      {fmtDateTime(ann.created_at, lang)} · {(ann.dismissed_by || []).length > 0 ? d.ann_seen.replace("{n}", ann.dismissed_by.length) : d.ann_unseen}
                      {ann.attachment ? ` · ${ann.attachment.name}` : ""}
                    </p>
                  </div>
                  <button type="button" onClick={() => remove(ann.id)} className="ml-auto text-rose-500 hover:text-rose-300 shrink-0" data-testid={`ann-del-${ann.id}`} aria-label="Delete">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};